import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, useParams } from "react-router-dom";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import app from "./firebase";
import { SessionProvider } from "./context/SessionContext";
import HomePage from "./components/HomePage";
import Login from "./components/academics/Login";
import StudentCourseLineup from "./components/academics/StudentCourseLineup";
import SignInPage from "./components/students/SignInPage";
import StudentPasswordReset from "./components/students/StudentPasswordReset";
import EnrolledSignInPage from "./components/enrolled/SignInPage";
import PreRegistration from "./components/enrolled/PreRegistration";
import EnrolledDashboard from "./components/enrolled/EnrolledDashboard";
import VicePresidentDashboard from "./components/academics/VicePresidentDashboard";
import DeanDashboard from "./components/academics/DeanDashboard";
import ProgramHeadDashboard from "./components/academics/ProgramHeadDashboard";
import ProfessorDashboard from "./components/academics/ProfessorDashboard";
import ProfessorSchedule from "./components/academics/ProfessorSchedule";
import ProfessorGrading from "./components/academics/ProfessorGrading";
import CourseLineup from "./components/students/CourseLineup";
import RegistarLogin from "./components/registar/Login";
import RegistarDashboard from "./components/registar/Dashboard";
import AnnouncementControl from "./components/registar/AnnouncementControl";
import RegistarAnalytics from "./components/registar/Analytics";
import RegistarCurriculum from "./components/registar/Curriculum";
import RegisterCurriculum from "./components/registar/RegisterCurriculum";
import StudentDashboard from "./components/students/StudentDashboard";
import AdmissionLogin from "./components/admission/Login";
import AdmissionDashboard from "./components/admission/AdmissionDashboard";
import ReviewApplication from "./components/admission/ReviewApplication";
import FinancesLogin from "./components/finances/Login";
import RoomManagementLogin from "./components/roommanagement/RoomManagementLogin";
import FinancesDashboard from "./components/finances/Dashboard";
import StudentSchedule from "./components/academics/StudentSchedule";
import AssessmentFee from "./components/finances/AssessmentFee";
import EnrollmentFee from "./components/finances/EnrollmentFee";
import ReviewEnrollment from "./components/registar/ReviewEnrollment";
import RoomDashboard from "./components/roommanagement/RoomDashboard";
import RegistrarMiscellaneousFees from "./components/registar/MiscellaneousFees";
import StudentsRequirements from "./components/registar/StudentsRequirements";
import CurriculumOverview from "./components/enrolled/CurriculumOverview";
import Announcements from "./components/admin/Announcements";
import AuditLogs from "./components/admin/AuditLogs";
import LoginHistory from "./components/admin/LoginHistory";
import ForgotPassword from "./components/common/ForgotPassword";
import ClassSchedule from "./components/enrolled/ClassSchedule";
import ServicesLogin from "./components/services/Login";
import AdminLogin from "./components/admin/AdminLogin";
import AdminDashboard from "./components/admin/AdminDashboard";
import Department from "./components/admin/Department";
import UserManagement from "./components/admin/users/UserManagement";
import AddUser from "./components/admin/users/AddUser";
import EditUser from "./components/admin/users/EditUser";
import ResetPassword from "./components/admin/users/ResetPassword";
import EnrolledCORPage from "./components/enrolled/EnrolledCOR";
import EnrolledCourseLineUp from "./components/enrolled/EnrolledCourseLineUp";
import PaymentOfFees from "./components/enrolled/PaymentOfFees";
import { useSession } from "./context/SessionContext";
import { subscribeRoleEvents } from "./auth/roleSession";

function StudentDashboardRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const { nameSlug } = useParams();
  const [student, setStudent] = useState(location.state?.student || null);
  const [loading, setLoading] = useState(!location.state?.student);

  useEffect(() => {
    if (!student && nameSlug) {
      setLoading(true);
      const fetchStudent = async () => {
        try {
          const db = getFirestore(app);
          const [firstName, lastName] = nameSlug.split('-');
          const { collection, query, where, getDocs } = await import("firebase/firestore");
          const q = query(
            collection(db, "students"),
            where("firstName", "==", firstName.replace(/\b\w/g, c => c.toUpperCase())),
            where("lastName", "==", lastName.replace(/\b\w/g, c => c.toUpperCase()))
          );
          const snap = await getDocs(q);
          if (!snap.empty) {
            const docSnap = snap.docs[0];
            setStudent({ ...docSnap.data(), id: docSnap.id });
          } else {
            navigate("/signin");
          }
        } catch (e) {
          navigate("/signin");
        }
        setLoading(false);
      };
      fetchStudent();
    }
  }, [nameSlug]);

  if (loading) return null;
  if (!student) return null;
  return <StudentDashboard student={student} onLogout={() => navigate("/signin")} />;
}

function StudentScheduleRoute() {
  const { studentId } = useParams();
  return <StudentSchedule studentId={studentId} />;
}

function CourseLineupRoute() {
  const { studentId } = useParams();
  return <CourseLineup studentId={studentId} />;
}

function roleRedirect(role) {
  if (role === "Room Manager") return "/room-management";
  if (role === "Registrar") return "/registar/dashboard";
  if (role === "Admission Counselor") return "/admission/dashboard";
  if (role === "Treasurer" || role === "Financial Officer") return "/finances/dashboard";
  if (role === "Vice President") return "/vp";
  if (role === "Dean") return "/dean";
  if (role === "Program Head") return "/program-head";
  if (role === "Professor") return "/professor";
  if (role === "System Administrator") return "/AdminDashboard";
  return "/";
}

function loginRedirect(roles) {
  if (roles.includes("System Administrator")) return "/admin-login";
  if (roles.includes("Registrar")) return "/registar-login";
  if (roles.includes("Admission Counselor")) return "/admission-login";
  if (roles.includes("Room Manager")) return "/room-management-login";
  if (roles.includes("Treasurer") || roles.includes("Financial Officer")) return "/finances";
  return "/academics";
}

function RoleGuard({ roles, children }) {
  const navigate = useNavigate();
  const { currentUser, userRole, loading, roleLoading } = useSession();

  useEffect(() => {
    if (loading || roleLoading) return;
    if (!currentUser || !userRole) {
      navigate(loginRedirect(roles), { replace: true });
      return;
    }
    if (!roles.includes(userRole)) {
      navigate(roleRedirect(userRole), { replace: true });
    }
  }, [currentUser, loading, navigate, roleLoading, roles, userRole]);

  if (loading || roleLoading || !currentUser || !roles.includes(userRole)) return null;
  return children;
}

function EnrolledSessionRedirector() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.pathname.toLowerCase().startsWith("/enrolled")) return undefined;
    return subscribeRoleEvents("Enrolled Student", (event) => {
      if (event.type === "logout") navigate("/enrolled/signin", { replace: true });
    });
  }, [location.pathname, navigate]);

  return null;
}

function DeploymentUpdateNotice() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const activeBundle = document.querySelector('script[src*="/static/js/main."]');
    const activeBundlePath = activeBundle ? new URL(activeBundle.src).pathname : "";
    if (!activeBundlePath) return undefined;

    let active = true;
    let checking = false;
    const checkForUpdate = async () => {
      if (checking || document.visibilityState === "hidden") return;
      checking = true;
      try {
        const response = await fetch(`/asset-manifest.json?deploy-check=${Date.now()}`, {
          cache: "no-store"
        });
        if (!response.ok) {
          throw new Error(`Deployment version check returned HTTP ${response.status}.`);
        }
        const manifest = await response.json();
        const latestBundlePath = typeof manifest.files?.["main.js"] === "string" ?
          new URL(manifest.files["main.js"], window.location.origin).pathname :
          "";
        if (active && latestBundlePath && latestBundlePath !== activeBundlePath) {
          const hasActiveWork = document.querySelector(
            '[role="dialog"], input:focus, textarea:focus, select:focus, [contenteditable="true"]:focus'
          );
          if (!hasActiveWork) {
            window.location.reload();
            return;
          }
          setUpdateAvailable(true);
        }
      } catch (error) {
        if (active) console.warn("Could not check for an EnrollEase update.", error);
      } finally {
        checking = false;
      }
    };

    checkForUpdate();
    const intervalId = window.setInterval(checkForUpdate, 60_000);
    window.addEventListener("focus", checkForUpdate);
    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", checkForUpdate);
    };
  }, []);

  if (!updateAvailable) return null;
  return (
    <div
      role="status"
      style={{
        position: "fixed",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        background: "#173f2a",
        color: "#fff",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
        fontSize: 14
      }}
    >
      <span>A new EnrollEase update is ready.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          padding: "7px 11px",
          border: 0,
          borderRadius: 5,
          background: "#fff",
          color: "#173f2a",
          fontWeight: 600,
          cursor: "pointer"
        }}
      >
        Update now
      </button>
    </div>
  );
}

function App() {
  return (
    <Router>
      <SessionProvider>
        <EnrolledSessionRedirector />
        <DeploymentUpdateNotice />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/academics" element={<Login />} />
          <Route path="/services" element={<ServicesLogin />} />
          <Route path="/vp" element={<RoleGuard roles={["Vice President"]}><VicePresidentDashboard /></RoleGuard>} />
          <Route path="/dean" element={<RoleGuard roles={["Dean"]}><DeanDashboard /></RoleGuard>} />
          <Route path="/program-head" element={<RoleGuard roles={["Program Head"]}><ProgramHeadDashboard /></RoleGuard>} />
          <Route path="/professor" element={<RoleGuard roles={["Professor"]}><ProfessorDashboard /></RoleGuard>} />
          <Route path="/professor/schedule" element={<RoleGuard roles={["Professor"]}><ProfessorSchedule /></RoleGuard>} />
          <Route path="/professor/grading" element={<RoleGuard roles={["Professor"]}><ProfessorGrading /></RoleGuard>} />
          <Route path="/program-head/student-course-lineup" element={<RoleGuard roles={["Program Head"]}><StudentCourseLineup /></RoleGuard>} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/student-reset-password" element={<StudentPasswordReset />} />
          <Route path="/enrolled/signin" element={<EnrolledSignInPage />} />
          <Route path="/enrolled/pre-registration" element={<PreRegistration />} />
          <Route path="/enrolled/dashboard" element={<EnrolledDashboard />} />
          <Route path="/registar" element={<RegistarLogin />} />
          <Route path="/registar-login" element={<RegistarLogin />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/registar/dashboard" element={<RoleGuard roles={["Registrar"]}><RegistarDashboard /></RoleGuard>}>
            <Route index element={<div>Welcome to Registar Home</div>} />
            <Route path="review-enrollment" element={<ReviewEnrollment />} />
            <Route path="miscellaneous-fees" element={<RegistrarMiscellaneousFees />} />
            <Route path="curriculum" element={<RegistarCurriculum />} />
            <Route path="curriculum/register" element={<RegisterCurriculum />} />
            <Route path="announcement" element={<AnnouncementControl />} />
            <Route path="students-requirements" element={<StudentsRequirements />} />
            <Route path="analytics" element={<RegistarAnalytics />} />
          </Route>
          <Route path="/student-portal/dashboard/:nameSlug" element={<StudentDashboardRoute />} />
          <Route path="/program-head/student-schedule/:studentId" element={<StudentScheduleRoute />} />
          <Route path="/enrollease/student-course-lineup/:studentId" element={<CourseLineupRoute />} />
          <Route path="/admission" element={<AdmissionLogin />} />
          <Route path="/admission-login" element={<AdmissionLogin />} />
          <Route path="/admission/dashboard" element={<RoleGuard roles={["Admission Counselor"]}><AdmissionDashboard /></RoleGuard>}>
            <Route path="review-application" element={<ReviewApplication />} />
          </Route>
          <Route path="/finances" element={<FinancesLogin />} />
          <Route path="/finances/dashboard" element={<RoleGuard roles={["Treasurer", "Financial Officer"]}><FinancesDashboard /></RoleGuard>}>
            <Route path="assessment-fee" element={<AssessmentFee />} />
            <Route path="enrollment-fee" element={<EnrollmentFee />} />
          </Route>
          <Route path="/room-management-login" element={<RoomManagementLogin />} />
          <Route path="/room-management" element={<RoleGuard roles={["Room Manager"]}><RoomDashboard /></RoleGuard>} />
          <Route path="/room-management/dashboard" element={<RoleGuard roles={["Room Manager"]}><RoomDashboard /></RoleGuard>} />
          <Route path="/enrolled/curriculum-overview" element={<CurriculumOverview />} />
          <Route path="/enrolled/class-schedule" element={<ClassSchedule />} />
          <Route path="/AdminDashboard" element={<RoleGuard roles={["System Administrator"]}><AdminDashboard /></RoleGuard>} />
          <Route path="/admin/departments" element={<RoleGuard roles={["System Administrator"]}><Department /></RoleGuard>} />
          <Route path="/admin/users" element={<RoleGuard roles={["System Administrator"]}><UserManagement /></RoleGuard>} />
          <Route path="/admin/users/add" element={<RoleGuard roles={["System Administrator"]}><AddUser /></RoleGuard>} />
          <Route path="/admin/users/edit/:userId" element={<RoleGuard roles={["System Administrator"]}><EditUser /></RoleGuard>} />
          <Route path="/admin/users/reset/:userId" element={<RoleGuard roles={["System Administrator"]}><ResetPassword /></RoleGuard>} />
          <Route path="/admin/announcements" element={<RoleGuard roles={["System Administrator"]}><Announcements /></RoleGuard>} />
          <Route path="/admin/audit" element={<RoleGuard roles={["System Administrator"]}><AuditLogs /></RoleGuard>} />
          <Route path="/admin/login-history" element={<RoleGuard roles={["System Administrator"]}><LoginHistory /></RoleGuard>} />
          <Route path="/enrolled/payment-of-fees" element={<PaymentOfFees />} />
          <Route path="/enrolled/cor" element={<EnrolledCORPage />} />
          <Route path="/enrolled/course-lineup" element={<EnrolledCourseLineUp />} />
        </Routes>
      </SessionProvider>
    </Router>
  );
}

export default App;
